import { useEffect, useState } from "react";
import { get, put, type Trip } from "../lib/api.ts";
import { setPhoneLocation } from "../lib/session.ts";
import { Card, reason } from "../ui/index.ts";

/**
 * Each person's own consent, versioned: the call states the version it was based on. It is its
 * own page under the trip, opened by the "Your sharing" row on Settings, and it is also what the
 * desktop Overview shows inline -- one component, so the two can never drift apart.
 */
export function YourSharing({ trip }: { trip: Trip }) {
  const [error, setError] = useState<string | null>(null);
  const [consent, setConsent] = useState<Record<string, boolean | null>>({
    MEDIA: null,
    ROUTE: null,
  });
  useEffect(() => {
    for (const scope of ["MEDIA", "ROUTE"]) {
      get<{ decision: string }>(`/v1/trips/${trip.id}/consents/${scope}`)
        .then((c) => setConsent((x) => ({ ...x, [scope]: c.decision === "GRANTED" })))
        .catch(() => setConsent((x) => ({ ...x, [scope]: false })));
    }
  }, [trip.id]);

  async function toggle(scope: "MEDIA" | "ROUTE") {
    setError(null);
    try {
      const current = await get<{ version: number }>(`/v1/trips/${trip.id}/consents/${scope}`);
      await put(`/v1/trips/${trip.id}/consents/${scope}`, {
        decision: consent[scope] ? "WITHDRAWN" : "GRANTED",
        baseVersion: current.version,
      });
      if (scope === "ROUTE")
        await setPhoneLocation(trip.id, !consent[scope]).catch(() => undefined);
      setConsent((x) => ({ ...x, [scope]: !x[scope] }));
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  return (
    <Card title="Your sharing">
      <p className="muted small" style={{ margin: 0 }}>
        Only footage taken during the trip is shared automatically. If you share your route,
        everyone on this trip can see your full route.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="row wrap">
        <button
          className="button"
          onClick={() => toggle("MEDIA")}
          disabled={consent["MEDIA"] === null}
        >
          {consent["MEDIA"] ? "Stop sharing my footage" : "Share my footage"}
        </button>
        <button
          className="button"
          onClick={() => toggle("ROUTE")}
          disabled={consent["ROUTE"] === null}
        >
          {consent["ROUTE"] ? "Stop sharing my route" : "Share my route"}
        </button>
      </div>
    </Card>
  );
}
